import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Lock, Save, Eye, EyeOff, Settings, X, Check } from 'lucide-react';
import { toast } from '../utils/toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Edit Onboarding Settings Modal Component
const EditOnboardingSettingsModal = ({ isOpen, onClose, currentSettings, onSave }) => {
  const [dashboardItems, setDashboardItems] = useState({
    rewardsRecognition: { enabled: true, label: 'Rewards & Recognition', mandatory: true },
    activity: { enabled: true, label: 'Activity', mandatory: true },
    feedback: { enabled: true, label: 'Feedback', mandatory: true },
    auditCompliance: { enabled: false, label: 'Audit & Compliance', mandatory: false },
    patientCare: { enabled: false, label: 'Patient Care', mandatory: false },
    staffPerformance: { enabled: false, label: 'Staff Performance', mandatory: false },
    costEffectiveness: { enabled: false, label: 'Cost-Effectiveness', mandatory: false },
  });
  const [orgSetup, setOrgSetup] = useState('custom');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentSettings && currentSettings.dashboard_items) {
      // Merge current settings with defaults
      const mergedItems = { ...dashboardItems };
      Object.keys(currentSettings.dashboard_items).forEach(key => {
        if (mergedItems[key]) {
          mergedItems[key] = {
            ...mergedItems[key],
            enabled: currentSettings.dashboard_items[key].enabled
          };
        }
      });
      setDashboardItems(mergedItems);
      setOrgSetup(currentSettings.organizational_setup || 'custom');
    }
  }, [currentSettings]);

  const toggleItem = (key) => {
    if (dashboardItems[key].mandatory) return;
    setDashboardItems(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        dashboard_items: Object.entries(dashboardItems).reduce((acc, [key, value]) => {
          acc[key] = { enabled: value.enabled, label: value.label, mandatory: value.mandatory };
          return acc;
        }, {}),
        organizational_setup: orgSetup
      };

      await axios.put(`${API}/hospital/setup-config`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Onboarding settings updated successfully!');
      onSave(payload);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Edit Onboarding Settings</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Dashboard Modules */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dashboard Modules</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select which modules to enable on your dashboard. Mandatory modules cannot be disabled.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(dashboardItems).map(([key, item]) => (
                <div
                  key={key}
                  onClick={() => toggleItem(key)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    item.enabled
                      ? 'border-[#0056B3] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${item.mandatory ? 'cursor-not-allowed opacity-80' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${item.enabled ? 'text-[#0056B3]' : 'text-gray-700'}`}>
                      {item.label}
                    </span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      item.enabled ? 'bg-[#0056B3] border-[#0056B3]' : 'border-gray-300'
                    }`}>
                      {item.enabled && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  {item.mandatory && (
                    <span className="text-xs text-gray-500 mt-1 block">Mandatory</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Organizational Setup */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Organizational Setup</h3>
            <div className="space-y-3">
              <div
                onClick={() => setOrgSetup('default')}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  orgSetup === 'default'
                    ? 'border-[#0056B3] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`font-medium ${orgSetup === 'default' ? 'text-[#0056B3]' : 'text-gray-700'}`}>
                      Default Setup
                    </span>
                    <p className="text-sm text-gray-500 mt-1">Use predefined departments and roles</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    orgSetup === 'default' ? 'bg-[#0056B3] border-[#0056B3]' : 'border-gray-300'
                  }`}>
                    {orgSetup === 'default' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
              </div>

              <div
                onClick={() => setOrgSetup('custom')}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  orgSetup === 'custom'
                    ? 'border-[#0056B3] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`font-medium ${orgSetup === 'custom' ? 'text-[#0056B3]' : 'text-gray-700'}`}>
                      Custom Setup
                    </span>
                    <p className="text-sm text-gray-500 mt-1">Configure your own departments and roles</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    orgSetup === 'custom' ? 'bg-[#0056B3] border-[#0056B3]' : 'border-gray-300'
                  }`}>
                    {orgSetup === 'custom' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              ⚠️ Note: Training Cost Survey settings are managed separately and are not editable here.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0056B3] hover:bg-blue-700">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Onboarding settings state (for Hospital Admin)
  const [onboardingSettings, setOnboardingSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const [formData, setFormData] = useState({
    communication_email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setFormData({
        ...formData,
        communication_email: parsedUser.communication_email || parsedUser.email || ''
      });
      
      // Fetch onboarding settings if Hospital Admin
      if (parsedUser.role === 'Hospital Admin') {
        fetchOnboardingSettings();
      }
    }
  }, []);
  
  const fetchOnboardingSettings = async () => {
    setLoadingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital/setup-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnboardingSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch onboarding settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleUpdateProfile = async () => {
    // Validation
    if (!formData.communication_email) {
      toast.error('Communication email is required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.communication_email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/profile/update-email`, {
        communication_email: formData.communication_email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update localStorage
      const updatedUser = { ...user, communication_email: formData.communication_email };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      toast.success('Communication email updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    }
    setLoading(false);
  };

  const handleUpdatePassword = async () => {
    // Validation
    if (!formData.current_password) {
      toast.error('Current password is required');
      return;
    }
    if (!formData.new_password) {
      toast.error('New password is required');
      return;
    }
    if (formData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/profile/update-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Password updated successfully!');
      
      // Clear password fields
      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    }
    setLoading(false);
  };

  if (!user) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading profile...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">MY PROFILE</h1>
          <p className="text-gray-600">Manage your account information and security settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information (Read-Only) */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <User className="w-6 h-6 text-[#0056B3]" />
                <h2 className="text-[22px] font-semibold text-gray-900">Profile Information</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-gray-700">Full Name</Label>
                  <Input
                    value={user.full_name || user.name || 'N/A'}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <Label className="text-gray-700">Username (Login Email)</Label>
                  <Input
                    value={user.email || user.username || 'N/A'}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <Label className="text-gray-700">Role</Label>
                  <Input
                    value={user.role || 'N/A'}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                {user.hospital_name && (
                  <div>
                    <Label className="text-gray-700">Institution</Label>
                    <Input
                      value={user.hospital_name}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                )}
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  ℹ️ These fields are read-only. Contact your administrator to update this information.
                </p>
              </div>
            </Card>

            {/* Communication Email */}
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Mail className="w-6 h-6 text-[#0056B3]" />
                <h2 className="text-[22px] font-semibold text-gray-900">Communication Email</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-gray-700">Communication Email Address</Label>
                  <Input
                    type="email"
                    placeholder="Enter your preferred email for notifications"
                    value={formData.communication_email}
                    onChange={(e) => setFormData({ ...formData, communication_email: e.target.value })}
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    This email will be used for system notifications and communications.
                  </p>
                </div>

                <Button
                  onClick={handleUpdateProfile}
                  disabled={loading}
                  className="bg-[#0056B3] hover:bg-blue-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Updating...' : 'Update Email'}
                </Button>
              </div>
            </Card>

            {/* Password Update */}
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Lock className="w-6 h-6 text-[#0056B3]" />
                <h2 className="text-[22px] font-semibold text-gray-900">Change Password</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-gray-700">Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={formData.current_password}
                      onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700">New Password</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password (min. 6 characters)"
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  />
                </div>

                <div>
                  <Label className="text-gray-700">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter new password"
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                    </button>
                  </div>
                </div>

                <Button
                  onClick={handleUpdatePassword}
                  disabled={loading}
                  className="bg-[#0056B3] hover:bg-blue-700"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ After changing your password, you'll remain logged in on this device.
                </p>
              </div>
            </Card>

            {/* Onboarding Settings - Hospital Admin Only */}
            {user.role === 'Hospital Admin' && (
              <Card className="p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <Settings className="w-6 h-6 text-[#0056B3]" />
                  <h2 className="text-[22px] font-semibold text-gray-900">Onboarding Settings</h2>
                </div>

                {loadingSettings ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading settings...</span>
                  </div>
                ) : onboardingSettings?.setup_completed ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-700">Dashboard Modules</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {onboardingSettings.dashboard_items && 
                          Object.entries(onboardingSettings.dashboard_items)
                            .filter(([_, item]) => item.enabled)
                            .map(([key, item]) => (
                              <span 
                                key={key}
                                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                              >
                                {item.label}
                              </span>
                            ))
                        }
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-700">Organizational Setup</Label>
                      <p className="text-gray-900 font-medium capitalize mt-1">
                        {onboardingSettings.organizational_setup || 'Custom'}
                      </p>
                    </div>

                    <Button
                      onClick={() => setShowSettingsModal(true)}
                      className="bg-[#0056B3] hover:bg-blue-700"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Onboarding Settings
                    </Button>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      ℹ️ No onboarding settings found. Complete the initial setup to configure your dashboard.
                    </p>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar - Quick Info */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Summary</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Account Type:</span>
                  <span className="font-medium text-gray-900">{user.role}</span>
                </div>
                
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Login Email:</span>
                  <span className="font-medium text-gray-900 truncate ml-2" data-tooltip={user.email}>
                    {user.email?.substring(0, 20)}...
                  </span>
                </div>

                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium text-green-600">Active</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Security Tips</h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Use a strong, unique password</li>
                  <li>• Update password regularly</li>
                  <li>• Never share your credentials</li>
                  <li>• Keep communication email current</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Edit Onboarding Settings Modal */}
      <EditOnboardingSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        currentSettings={onboardingSettings}
        onSave={(newSettings) => {
          setOnboardingSettings(prev => ({
            ...prev,
            ...newSettings
          }));
        }}
      />
    </AdminLayout>
  );
};

export default ProfilePage;
